import { c as _c } from "react/compiler-runtime";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { Box } from "@mui-wrapper";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { DownloadUtils } from "@stellar/utils";
import { getFileExtension, isImageFileType } from "@utils/file-utils";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { Dialog, DeleteIcon, DownloadIcon, Text } from "@flat-ui";
/**
 * Component that rendering action buttons for managing an attachment item,
 * including downloading the attachment and deleting it (if the user has permission).
 *
 * @param {AttachmentItemActionsProps} Props - The properties for the component, including the attachment data,
 * a function to update the downloading state and a function to control the delete modal visibility.
 * @returns {JSX.Element} The rendered component.
 */
export function AttachmentItemActions(t0) {
  const $ = _c(52);
  const {
    attachment,
    setIsDownloading,
    eventSource,
    buttonSize,
    iconSize,
    iconBtnProps,
    setSelectedAttachments
  } = t0;
  const {
    hasPermissionToEditMarkup,
    projectId,
    selectedMarkup,
    setIsMarkupUpdating,
    attachments,
    updateAttachments
  } = useMarkupContext();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    deleteAttachmentToMarkup
  } = useProjectMarkupUpdate(t1);
  const {
    refreshAttachmentIfExpired,
    isAttachmentExpired
  } = useRefreshAttachment();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    trackEvent
  } = useTrackEvent();
  let fileExtension;
  let t2;
  if ($[2] !== attachment.name) {
    fileExtension = getFileExtension(attachment.name);
    t2 = isImageFileType(fileExtension);
    $[2] = attachment.name;
    $[3] = fileExtension;
    $[4] = t2;
  } else {
    fileExtension = $[3];
    t2 = $[4];
  }
  const isImage = t2;
  let t3;
  if ($[5] !== attachment || $[6] !== eventSource || $[7] !== fileExtension || $[8] !== isAttachmentExpired || $[9] !== isImage || $[10] !== refreshAttachmentIfExpired || $[11] !== setIsDownloading || $[12] !== trackEvent) {
    t3 = async function downloadAndSaveFile() {
      trackEvent({
        name: `${AnnotationEvents.downloadAttachment}`,
        props: {
          eventSource,
          fileType: isImage ? "image" : "other",
          fileExtension
        }
      });
      const isExpired = isAttachmentExpired(attachment);
      let currentAttachment = attachment;
      if (isExpired) {
        setIsDownloading(true);
        try {
          const updatedAttachments = await refreshAttachmentIfExpired([attachment.id]);
          currentAttachment = updatedAttachments[0];
        } catch {
          setIsDownloading(false);
          return;
        }
      }
      if (!currentAttachment.signedUri || !currentAttachment.signedUriExpiresOn) {
        return;
      }
      const fileBlob = await DownloadUtils.downloadFilePromise(currentAttachment.signedUri, {
        onFileDownloadStart: () => setIsDownloading(true),
        onFileDownloadEnd: () => setIsDownloading(false)
      });
      const fileUrl = URL.createObjectURL(fileBlob);
      DownloadUtils.downloadFile(attachment.name, fileUrl);
    };
    $[5] = attachment;
    $[6] = eventSource;
    $[7] = fileExtension;
    $[8] = isAttachmentExpired;
    $[9] = isImage;
    $[10] = refreshAttachmentIfExpired;
    $[11] = setIsDownloading;
    $[12] = trackEvent;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const downloadAndSaveFile = t3;
  let t4;
  if ($[14] !== attachment || $[15] !== attachments || $[16] !== deleteAttachmentToMarkup || $[17] !== eventSource || $[18] !== fileExtension || $[19] !== isImage || $[20] !== selectedMarkup || $[21] !== setIsMarkupUpdating || $[22] !== setSelectedAttachments || $[23] !== trackEvent || $[24] !== updateAttachments) {
    t4 = async function deleteAttachment() {
      trackEvent({
        name: `${AnnotationEvents.deleteAttachment}`,
        props: {
          eventSource,
          fileType: isImage ? "image" : "other",
          fileExtension
        }
      });
      setIsDeleting(true);
      setIsMarkupUpdating(true);
      const mutation = await deleteAttachmentToMarkup(attachment);
      if (!mutation || !selectedMarkup) {
        setIsDeleting(false);
        setIsMarkupUpdating(false);
        return;
      }
      const updatedAttachments_0 = attachments.filter(attachment_0 => attachment_0.id !== mutation.elementId);
      updateAttachments(updatedAttachments_0);
      setSelectedAttachments(prevSelected => {
        if (!prevSelected.has(attachment.id)) {
          return prevSelected;
        }
        const updatedSet = new Set(prevSelected);
        updatedSet.delete(attachment.id);
        return updatedSet.size > 0 ? updatedSet : new Set();
      });
      setIsDeleting(false);
      setIsMarkupUpdating(false);
    };
    $[14] = attachment;
    $[15] = attachments;
    $[16] = deleteAttachmentToMarkup;
    $[17] = eventSource;
    $[18] = fileExtension;
    $[19] = isImage;
    $[20] = selectedMarkup;
    $[21] = setIsMarkupUpdating;
    $[22] = setSelectedAttachments;
    $[23] = trackEvent;
    $[24] = updateAttachments;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const deleteAttachment = t4;
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      display: "flex"
    };
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== iconBtnProps) {
    t6 = iconBtnProps ? iconBtnProps : {};
    $[27] = iconBtnProps;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== buttonSize || $[30] !== downloadAndSaveFile || $[31] !== iconSize || $[32] !== t6) {
    t7 = <SphereIconButton buttonSize={buttonSize} iconSize={iconSize} component={DownloadIcon} onClick={downloadAndSaveFile} dataTestId="markup-side-panel-attachment-item-download-icon" iconButtonProps={t6} />;
    $[29] = buttonSize;
    $[30] = downloadAndSaveFile;
    $[31] = iconSize;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== buttonSize || $[35] !== hasPermissionToEditMarkup || $[36] !== iconBtnProps || $[37] !== iconSize) {
    t8 = hasPermissionToEditMarkup && <><SphereActionDivider /><SphereIconButton buttonSize={buttonSize} iconSize={iconSize} component={DeleteIcon} onClick={() => setIsDeleteModalOpen(true)} dataTestId="markup-side-panel-attachment-item-delete-icon" iconButtonProps={iconBtnProps ? iconBtnProps : {}} /></>;
    $[34] = buttonSize;
    $[35] = hasPermissionToEditMarkup;
    $[36] = iconBtnProps;
    $[37] = iconSize;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsDeleteModalOpen(false);
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      color: "text.primary"
    };
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== attachment.name) {
    t11 = <Text variant="bodyM" data-testid="delete-annotation-attachment-dialog-content" sx={t10}>This will remove the attachment "<var id="attachment-name">{attachment.name}</var>" from your project.</Text>;
    $[41] = attachment.name;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== deleteAttachment || $[44] !== isDeleteModalOpen || $[45] !== isDeleting || $[46] !== t11) {
    t12 = <Dialog title="Delete attachment?" confirmText="Delete" open={isDeleteModalOpen} onConfirm={deleteAttachment} onCancel={t9} showSpinner={isDeleting} variant="danger">{t11}</Dialog>;
    $[43] = deleteAttachment;
    $[44] = isDeleteModalOpen;
    $[45] = isDeleting;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t12 || $[49] !== t7 || $[50] !== t8) {
    t13 = <Box sx={t5}>{t7}{t8}{t12}</Box>;
    $[48] = t12;
    $[49] = t7;
    $[50] = t8;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}